import { NavLink } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectSidebarOpen, closeSidebar } from '../store/slices/uiSlice';
import { selectIsAuthenticated, selectIsAdmin, selectCurrentUser } from '../store/slices/authSlice';

function Sidebar() {
  const dispatch = useDispatch();
  const sidebarOpen = useSelector(selectSidebarOpen);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAdmin = useSelector(selectIsAdmin);
  const user = useSelector(selectCurrentUser);

  return (
    <>
      {/* Desktop Sidebar - Fixed 80px width with icons only */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-full w-[80px] flex-col items-center py-6 bg-white dark:bg-gray-900 z-[60] border-r border-gray-100 dark:border-gray-800 shadow-sm">
        {/* Logo */}
        <a href="/" className="w-12 h-12 flex items-center justify-center text-primary-600 mb-8 cursor-pointer hover:scale-110 transition-transform duration-200">
          <img 
            src="https://res.cloudinary.com/ds52btbjy/image/upload/v1772812120/logo_full_hcwxaa.png" 
            alt="Logo" 
            className="w-10 h-10 object-contain" 
          />
        </a>

        {/* Navigation */}
        <nav className="flex flex-col gap-3 w-full px-2 items-center">
          <NavLink
            to="/"
            className={({ isActive }) =>
              `tooltip-enhanced w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 ${
                isActive
                  ? 'bg-gradient-to-br from-gray-800 to-gray-900 text-white shadow-lg scale-110'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 hover:scale-110'
              }`
            }
            title="Trang chủ"
          >
            <span className="material-symbols-rounded text-[26px]">home</span>
          </NavLink>

          {isAuthenticated && (
            <NavLink
              to="/add"
              className={({ isActive }) =>
                `tooltip-enhanced w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg scale-110'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 hover:scale-110'
                }`
              }
              title="Tạo prompt"
            >
              <span className="material-symbols-rounded text-[26px]">add_box</span>
            </NavLink>
          )}

          {isAuthenticated && (
            <NavLink
              to="/profile"
              className={({ isActive }) =>
                `tooltip-enhanced w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg scale-110'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-600 hover:scale-110'
                }`
              }
              title="Trang cá nhân"
            >
              <span className="material-symbols-rounded text-[26px]">person</span>
            </NavLink>
          )}

          <div className="w-full h-px bg-gray-200 dark:bg-gray-700 my-2"></div>

          <NavLink
            to="/about"
            className={({ isActive }) =>
              `tooltip-enhanced w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 ${
                isActive
                  ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg scale-110'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-600 hover:scale-110'
              }`
            }
            title="Giới thiệu"
          >
            <span className="material-symbols-rounded text-[22px]">info</span>
          </NavLink>

          {isAuthenticated && !user?.onboardingCompleted && (
            <NavLink
              to="/onboarding"
              className={({ isActive }) =>
                `tooltip-enhanced w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg scale-110'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:text-amber-600 hover:scale-110'
                }`
              }
              title="Khảo sát"
            >
              <span className="material-symbols-rounded text-[22px]">quiz</span>
            </NavLink>
          )}

          <NavLink
            to="/privacy-policy"
            className={({ isActive }) =>
              `tooltip-enhanced w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 ${
                isActive
                  ? 'bg-gradient-to-br from-gray-800 to-gray-900 text-white shadow-lg scale-110'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 hover:scale-110'
              }`
            }
            title="Chính sách"
          >
            <span className="material-symbols-rounded text-[22px]">shield</span>
          </NavLink>
        </nav>
      </aside>

      {/* Mobile Overlay */}
      <div
        id="mobile-overlay"
        className={`lg:hidden fixed inset-0 bg-black/50 z-[70] transition-opacity ${
          sidebarOpen ? 'opacity-100' : 'opacity-0 hidden'
        }`}
        onClick={() => dispatch(closeSidebar())}
      />

      {/* Mobile Sidebar - 280px width with full labels */}
      <aside
        id="mobile-sidebar"
        className={`lg:hidden fixed left-0 top-0 h-full w-[280px] flex flex-col bg-white dark:bg-gray-900 z-[80] border-r border-gray-100 dark:border-gray-800 shadow-2xl transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Mobile Header */}
        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img 
              src="https://res.cloudinary.com/ds52btbjy/image/upload/v1772812120/logo_full_hcwxaa.png" 
              alt="Logo" 
              className="w-10 h-10 object-contain" 
            />
            <span className="text-xl font-bold text-gray-800 dark:text-gray-100">PromptKeeper</span>
          </a>
          <button
            onClick={() => dispatch(closeSidebar())}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <span className="material-symbols-rounded text-gray-600 dark:text-gray-400">close</span>
          </button>
        </div>

        {/* Mobile Navigation */}
        <nav className="flex-1 flex flex-col gap-1 p-4">
          <NavLink
            to="/"
            onClick={() => dispatch(closeSidebar())}
            className={({ isActive }) =>
              `flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${
                isActive
                  ? 'bg-gray-900 dark:bg-gray-800 text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
              }`
            }
          >
            <span className="material-symbols-rounded text-[24px]">home</span>
            <span className="font-semibold">Trang chủ</span>
          </NavLink>


          {isAuthenticated && (
            <NavLink
              to="/add"
              onClick={() => dispatch(closeSidebar())}
              className={({ isActive }) =>
                `flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600'
                }`
              }
            >
              <span className="material-symbols-rounded text-[24px]">add_box</span>
              <span className="font-semibold">Tạo prompt</span>
            </NavLink>
          )}

          {isAuthenticated && (
            <NavLink
              to="/profile"
              onClick={() => dispatch(closeSidebar())}
              className={({ isActive }) =>
                `flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? 'bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-md'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-600'
                }`
              }
            >
              <span className="material-symbols-rounded text-[24px]">person</span>
              <span className="font-semibold">Trang cá nhân</span>
            </NavLink>
          )}

          <NavLink
            to="/about"
            onClick={() => dispatch(closeSidebar())}
            className={({ isActive }) =>
              `flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${
                isActive
                  ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-600'
              }`
            }
          >
            <span className="material-symbols-rounded text-[24px]">info</span>
            <span className="font-semibold">Giới thiệu</span>
          </NavLink>

          {isAuthenticated && !user?.onboardingCompleted && (
            <NavLink
              to="/onboarding"
              onClick={() => dispatch(closeSidebar())}
              className={({ isActive }) =>
                `flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? 'bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-md'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:text-amber-600'
                }`
              }
            >
              <span className="material-symbols-rounded text-[24px]">quiz</span>
              <span className="font-semibold">Khảo sát</span>
            </NavLink>
          )}

          <NavLink
            to="/privacy-policy"
            onClick={() => dispatch(closeSidebar())}
            className={({ isActive }) =>
              `flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${
                isActive
                  ? 'bg-gradient-to-br from-gray-800 to-gray-900 text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-800 dark:hover:text-gray-100'
              }`
            }
          >
            <span className="material-symbols-rounded text-[24px]">shield</span>
            <span className="font-semibold">Chính sách</span>
          </NavLink>
        </nav>
      </aside>
    </>
  );
}

export default Sidebar;
